import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import QuickreplyIcon from '@mui/icons-material/Quickreply';
import BoltIcon from '@mui/icons-material/Bolt';
import MicIcon from '@mui/icons-material/Mic';

const CARDS = [
    {
        id: 1,
        Icon: EmojiEventsIcon,
        title: 'Targeted Band Improvement',
        description:
            'Stop practicing blindly. Every mock test identifies specific areas holding you back from your target band.',
    },
    {
        id: 2,
        Icon: QuickreplyIcon,
        title: "Clear 'What to Fix Next'",
        description:
            'Each feedback report tells you exactly what to work on. No guessing, no generic advice – just actionable steps.',
    },
    {
        id: 3,
        Icon: BoltIcon,
        title: 'Evidence-Driven Progress',
        description:
            'Watch your scores improve with data. Track trends across attempts and see your weak areas transform into strengths.',
    },
    {
        id: 4,
        Icon: MicIcon,
        title: 'Reduced Exam Uncertainty',
        description:
            `Walk into your IELTS exam knowing you've practiced in the same format, with clear understanding of your level.`,
    },
];

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        padding: '64px 32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
            padding: '48px 16px',
        },
    },

    // ── Header ───────────────────────────────────────
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '18px',
    },
    badgeStar: {
        color: '#EE634E',
        fontSize: '0.8rem',
    },
    badgeText: {
        color: '#EE634E',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.8rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
    },
    heading: {
        color: '#FFFFFF',
        fontFamily: 'Mulish, sans-serif',
        fontWeight: 800,
        fontSize: '2.2rem',
        lineHeight: 1.25,
        textAlign: 'center',
        marginBottom: '14px',
        [theme.breakpoints.down('md')]: {
            fontSize: '1.6rem',
        },
    },
    subtitle: {
        color: '#E6E6E6',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.9rem',
        lineHeight: 1.65,
        textAlign: 'center',
        maxWidth: '420px',
        marginBottom: '48px',
    },

    // ── Card Grid ────────────────────────────────────
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '16px',
        width: '100%',
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: '1fr',
        },
    },
    card: {
        backgroundColor: '#333651',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: '14px',
        padding: '28px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
    },

    // ── Icon ─────────────────────────────────────────
    iconWrapper: {
        width: '48px',
        height: '48px',
        borderRadius: '10px',
        backgroundColor: 'rgba(238,99,78,0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    icon: {
        color: '#EE634E',
        fontSize: '1.5rem',
    },

    // ── Text ─────────────────────────────────────────
    cardTitle: {
        color: '#FFFFFF',
        fontFamily: 'Mulish, sans-serif',
        fontWeight: 700,
        fontSize: '1rem',
        lineHeight: 1.4,
    },
    cardDesc: {
        color: '#E6E6E6',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.875rem',
        lineHeight: 1.7,
    },
}));

function FeatureCard({ card }) {
    const classes = useStyles();
    const { Icon, title, description } = card;

    return (
        <Box className={classes.card}>
            <Box className={classes.iconWrapper}>
                <Icon className={classes.icon} />
            </Box>
            <Typography className={classes.cardTitle}>
                {title}
            </Typography>
            <Typography className={classes.cardDesc}>
                {description}
            </Typography>
        </Box>
    );
}

function WhyChooseUs() {
    const classes = useStyles();

    return (
        <Box className={classes.root}>

            {/* Badge */}
            <Box className={classes.badge}>
                <Typography component="span" className={classes.badgeStar}>✳</Typography>
                <Typography component="span" className={classes.badgeText}>
                    Why Choose Us
                </Typography>
            </Box>

            {/* Heading */}
            <Typography className={classes.heading}>
                Results, Not Just Practice
            </Typography>

            {/* Subtitle */}
            <Typography className={classes.subtitle}>
                IELTS BandBoost is built for one purpose: helping you reach your target score.
            </Typography>

            {/* 2×2 Card Grid */}
            <Box className={classes.grid}>
                {CARDS.map(card => (
                    <FeatureCard key={card.id} card={card} />
                ))}
            </Box>

        </Box>
    );
}

export default WhyChooseUs;
